import { getTicketMessages, getTickets, sendTicketReply, updateReadAt } from '@/services/user.service'
import {
    Container,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    InputAdornment,
    TableSortLabel,
    TextField,
    Typography,
    Button,
    Box,
    Divider,
    Paper,
    Avatar,
    IconButton,
    CircularProgress,
    Alert,
    Chip,
    Badge,
    Link,
} from '@mui/material'
import { ArrowBack, Send, Person, Support, Reply as ReplyIcon, Search, AttachFile } from '@mui/icons-material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const TicketScreen = () => {
    const { t, i18n } = useTranslation('tickets')
    const [tickets, setTickets] = useState([])
    const [selectedTicket, setSelectedTicket] = useState(null)
    const [replyText, setReplyText] = useState('')
    const [loading, setLoading] = useState(false)
    const [sendingReply, setSendingReply] = useState(false)
    const [error, setError] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [sortConfig, setSortConfig] = useState({ orderBy: 'CreatedAt', order: 'desc' })

    const init = async () => {
        try {
            setLoading(true)
            const res = await getTickets()
            console.log(res?.tickets || [])
            setTickets(res?.tickets || [])
        } catch (err) {
            setError(t('errors.loadTickets'))
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const hasUnreadAdminMessages = (ticket) => {
        const msgs = ticket?.Messages || ticket?.TicketMessages || []
        if (Array.isArray(msgs) && msgs.length) {
            return msgs.some((m) => m?.AgridUserId && !m?.ReadAt)
        }
        const threads = ticket?.TicketThreads || ticket?.Threads || []
        if (Array.isArray(threads) && threads.length) {
            return threads.some((t) => t?.AgridUserId && !t?.ReadAt)
        }
        return !!ticket?.HasUnreadThread
    }

    const handleTicketClick = async (ticket) => {
        setSelectedTicket({ ...ticket, Messages: ticket?.Messages || [] })
        setReplyText('')
        setError('')

        try {
            const messagesRes = await getTicketMessages(ticket.TicketId || ticket.DisplayedNumber)
            const messages = messagesRes?.messages || []
            setSelectedTicket((prev) => ({ ...prev, Messages: messages }))

            const unreadThreadIds = Array.from(
                new Set(
                    messages
                        .filter((m) => m?.AgridUserId && !m?.ReadAt)
                        .map((m) => m?.TicketThreadId || m?.ThreadId)
                        .filter(Boolean)
                )
            )
            if (unreadThreadIds.length) {
                await Promise.all(
                    unreadThreadIds.map((id) => updateReadAt(id).catch((e) => console.error('updateReadAt failed', e)))
                )
                setTickets((prev) =>
                    prev.map((t) =>
                        t.DisplayedNumber === ticket.DisplayedNumber ? { ...t, HasUnreadThread: false } : t
                    )
                )
            }
        } catch (e) {
            console.error('Failed to load messages or update read status', e)
        }
    }

    const handleBackToList = () => {
        setSelectedTicket(null)
        setReplyText('')
        setError('')
    }

    const handleSendReply = async () => {
        if (!replyText.trim()) return

        try {
            setSendingReply(true)
            await sendTicketReply(selectedTicket.TicketId || selectedTicket.DisplayedNumber, replyText)
            setReplyText('')
            // Reload messages to show the new reply
            let messages = await getTicketMessages(selectedTicket.TicketId || selectedTicket.DisplayedNumber)
            setSelectedTicket((prev) => ({
                ...prev,
                Messages: messages?.messages || [],
            }))
            // scroll messages to bottom
            const messagesContainer = document.getElementById('messages-container')
            if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight
            }
        } catch (err) {
            setError(t('errors.sendReply'))
            console.error(err)
        } finally {
            setSendingReply(false)
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return ''
        const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-GB'
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return ''
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const formatMessageText = (text) => {
        if (!text) return ''
        // Replace any HTML <br> or <br/> tags with actual newline characters
        return text.replace(/<br\s*\/?>/gi, '\n')
    }

    // Parse [text](url) markdown links into safe HTML, preserving line breaks.
    // Escapes any user-provided HTML and only allows http/https URLs to defend against XSS.
    const parseMessageHTML = (rawText) => {
        if (!rawText && rawText !== '') return ''

        const escapeHTML = (str) =>
            String(str)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;')

        const decodeHTMLEntities = (str) =>
            String(str)
                .replace(/&amp;/g, '&')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')

        const sanitizeUrl = (href) => {
            const decoded = decodeHTMLEntities(String(href || '').trim())
            const lower = decoded.toLowerCase()
            if (lower.startsWith('http://') || lower.startsWith('https://')) {
                return escapeHTML(decoded)
            }
            return '#'
        }

        const normalized = String(rawText).replace(/<br\s*\/?>/gi, '\n')
        let safe = escapeHTML(normalized)

        safe = safe.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, href) => {
            const safeHref = sanitizeUrl(href)
            return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer nofollow" style="color: inherit; text-decoration: underline;">${label}</a>`
        })

        safe = safe.replace(/\n/g, '<br/>')
        return safe
    }

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
                return 'success'
            case 'pending':
                return 'warning'
            case 'closed':
                return 'default'
            default:
                return 'primary'
        }
    }

    const getSiteNames = (ticket) => {
        if (!ticket) return []

        if (Array.isArray(ticket.TicketSites) && ticket.TicketSites.length) {
            const names = ticket.TicketSites.map(
                (site) => site?.Name || site?.SiteName || site?.Site?.Name || site?.Site?.SiteName
            )
                .filter((name) => typeof name === 'string' && name.trim())
                .map((name) => name.trim())
            if (names.length) {
                return Array.from(new Set(names))
            }
        }

        if (typeof ticket.SiteName === 'string' && ticket.SiteName.trim()) {
            return [ticket.SiteName.trim()]
        }

        if (typeof ticket.Site?.Name === 'string' && ticket.Site.Name.trim()) {
            return [ticket.Site.Name.trim()]
        }

        if (typeof ticket.Site === 'string' && ticket.Site.trim()) {
            return [ticket.Site.trim()]
        }

        if (typeof ticket.SiteLabel === 'string' && ticket.SiteLabel.trim()) {
            return [ticket.SiteLabel.trim()]
        }

        return []
    }

    const extractMessageFiles = (message = {}) => {
        const fileSources = [
            message?.TicketThreadFiles,
            message?.TicketMessageFiles,
            message?.ThreadFiles,
            message?.Files,
            message?.Attachments,
        ].filter((files) => Array.isArray(files) && files.length)

        if (!fileSources.length) {
            return []
        }

        const seen = new Set()
        return fileSources
            .flat()
            .filter((file) => {
                const key =
                    file?.TicketThreadFileId ||
                    file?.TicketMessageFileId ||
                    file?.ThreadFileId ||
                    file?.FileId ||
                    file?.id ||
                    file?.Id ||
                    file?.File ||
                    file?.Url ||
                    file?.url

                if (!key) return true

                if (seen.has(key)) {
                    return false
                }
                seen.add(key)
                return true
            })
    }

    const getFileUrl = (file) => file?.File || file?.Url || file?.url || ''

    const getFileName = (file) =>
        file?.Name ||
        file?.FileName ||
        file?.filename ||
        file?.OriginalName ||
        file?.File ||
        file?.Url ||
        file?.url ||
        t('details.messages.file')

    const handleSort = (property) => {
        setSortConfig((prev) => {
            if (prev.orderBy === property) {
                return { orderBy: property, order: prev.order === 'asc' ? 'desc' : 'asc' }
            }

            return {
                orderBy: property,
                order: property === 'CreatedAt' ? 'desc' : 'asc',
            }
        })
    }

    const filteredTickets = useMemo(() => {
        const searchTerm = searchQuery.trim().toLowerCase()
        const { orderBy, order } = sortConfig

        const filterTickets = (list) => {
            if (!searchTerm) return [...list]

            return list.filter((ticket) => {
                const title = (ticket?.Title || '').toLowerCase()
                const number = (ticket?.DisplayedNumber || '').toString().toLowerCase()
                return title.includes(searchTerm) || number.includes(searchTerm)
            })
        }

        const getComparableValue = (ticket) => {
            switch (orderBy) {
                case 'DisplayedNumber':
                    return ticket?.DisplayedNumber ?? ''
                case 'Title':
                    return ticket?.Title ?? ''
                case 'Status':
                    return ticket?.Status ?? ''
                case 'Site':
                    return getSiteNames(ticket)[0] ?? ''
                case 'CreatedAt':
                default:
                    return ticket?.CreatedAt ?? ''
            }
        }

        const filtered = filterTickets(tickets)

        if (!orderBy) {
            return filtered
        }

        return [...filtered].sort((a, b) => {
            const aValue = getComparableValue(a)
            const bValue = getComparableValue(b)

            if (orderBy === 'CreatedAt') {
                const aDate = aValue ? new Date(aValue).getTime() : 0
                const bDate = bValue ? new Date(bValue).getTime() : 0
                return order === 'asc' ? aDate - bDate : bDate - aDate
            }

            const normalize = (value) => (value ?? '').toString().toLowerCase()
            const aString = normalize(aValue)
            const bString = normalize(bValue)

            if (aString < bString) return order === 'asc' ? -1 : 1
            if (aString > bString) return order === 'asc' ? 1 : -1
            return 0
        })
    }, [tickets, searchQuery, sortConfig])

    useEffect(() => {
        init()
    }, [])

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        )
    }

    // Show ticket details and messages view
    if (selectedTicket) {
        return (
            <Container>
                {/* Header with back button */}
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={handleBackToList} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1">
                        {t('details.title', { number: selectedTicket.DisplayedNumber })}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Ticket Details Card */}
                <Card sx={{ mb: 3 }}>
                    <CardHeader
                        title={selectedTicket.Title}
                        subheader={t('details.createdAt', { date: formatDate(selectedTicket.CreatedAt) })}
                        action={
                            selectedTicket.Status && (
                                <Chip
                                    label={selectedTicket.Status}
                                    color={getStatusColor(selectedTicket.Status)}
                                    size="small"
                                />
                            )
                        }
                        subheaderTypographyProps={{
                            fontSize: '0.75rem', // or any size you want
                            color: 'text.secondary',
                        }}
                    />
                    {selectedTicket.Description &&
                        selectedTicket.Description !== selectedTicket.Messages?.[0]?.Message && (
                            <CardContent>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                    {selectedTicket.Description}
                                </Typography>
                            </CardContent>
                        )}
                </Card>

                {/* Messages Section */}
                <Card>
                    <CardHeader title={t('details.messages.title')} titleTypographyProps={{ variant: 'h6' }} />
                    <CardContent>
                        <>
                            {/* Messages List */}
                            <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 3 }}>
                                {selectedTicket?.Messages.length === 0 ? (
                                    <Typography color="text.secondary" textAlign="center" py={3}>
                                        {t('details.messages.empty')}
                                    </Typography>
                                ) : (
                                    selectedTicket.Messages.map((message, index) => {
                                        const isFromSupport = message?.AgridUserId
                                        const files = extractMessageFiles(message)
                                        return (
                                            <Box
                                                key={message.id || index}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: isFromSupport ? 'flex-end' : 'flex-start',
                                                    mb: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: '70%',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1,
                                                        flexDirection: isFromSupport ? 'row-reverse' : 'row',
                                                    }}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: isFromSupport ? 'primary.main' : 'secondary.main',
                                                            width: 32,
                                                            height: 32,
                                                        }}
                                                    >
                                                        {isFromSupport ? (
                                                            <Support fontSize="small" />
                                                        ) : (
                                                            <Person fontSize="small" />
                                                        )}
                                                    </Avatar>
                                                    <Paper
                                                        elevation={1}
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor: isFromSupport ? 'primary.50' : 'grey.100',
                                                            borderRadius: 2,
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Box mb={1}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: isFromSupport
                                                                        ? 'primary.dark'
                                                                        : 'text.secondary',
                                                                }}
                                                            >
                                                                {message.senderName ||
                                                                    (isFromSupport
                                                                        ? t('details.messages.support')
                                                                        : t('details.messages.you'))}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                {formatDate(message.CreatedAt || message.date)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ wordBreak: 'break-word' }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: parseMessageHTML(message.Message),
                                                            }}
                                                        />
                                                        {files.length > 0 && (
                                                            <Box
                                                                mt={1}
                                                                display="flex"
                                                                flexDirection="column"
                                                                gap={0.5}
                                                                sx={{ wordBreak: 'break-word' }}
                                                            >
                                                                {/* <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={0.5}
                                                                    color="text.secondary"
                                                                >
                                                                    <AttachFile fontSize="small" />
                                                                    <Typography variant="caption">
                                                                        {t('details.messages.attachments')}
                                                                    </Typography>
                                                                </Box> */}
                                                                {files.map((file, fileIndex) => {
                                                                    const href = getFileUrl(file)
                                                                    if (!href) return null
                                                                    const key =
                                                                        file?.TicketThreadFileId ||
                                                                        file?.TicketMessageFileId ||
                                                                        file?.ThreadFileId ||
                                                                        file?.FileId ||
                                                                        file?.id ||
                                                                        `${index}-${fileIndex}`

                                                                    return (
                                                                        <Link
                                                                            key={key}
                                                                            href={href}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            underline="hover"
                                                                            sx={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                color: 'text.primary',
                                                                            }}
                                                                        >
                                                                            <AttachFile fontSize="small" />
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ wordBreak: 'break-all' }}
                                                                            >
                                                                                {getFileName(file)}
                                                                            </Typography>
                                                                        </Link>
                                                                    )
                                                                })}
                                                            </Box>
                                                        )}
                                                    </Paper>
                                                </Box>
                                            </Box>
                                        )
                                    })
                                )}
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* Reply Section */}
                            <Box>
                                {selectedTicket.Status === 'closed' && (
                                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                        {t('details.closedTicketWarning')}
                                    </Alert>
                                )}
                                {/* <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                    <ReplyIcon sx={{ mr: 1 }} />
                                    Répondre
                                </Typography> */}
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    placeholder={t('form.replyPlaceholder')}
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    disabled={sendingReply || selectedTicket.Status === 'closed'}
                                />
                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        variant="contained"
                                        startIcon={sendingReply ? <CircularProgress size={16} /> : <Send />}
                                        onClick={handleSendReply}
                                        disabled={
                                            !replyText.trim() || sendingReply || selectedTicket.Status === 'closed'
                                        }
                                    >
                                        {sendingReply ? t('form.sending') : t('form.send')}
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    </CardContent>
                </Card>
            </Container>
        )
    }

    // Show tickets list view
    return (
        <Container>
            <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                    {t('list.title')}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Paper>
                    <Box p={2} pb={0}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('list.search.label')}
                            placeholder={t('list.search.placeholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sortDirection={sortConfig.orderBy === 'DisplayedNumber' ? sortConfig.order : false}
                                >
                                    <TableSortLabel
                                        active={sortConfig.orderBy === 'DisplayedNumber'}
                                        direction={sortConfig.orderBy === 'DisplayedNumber' ? sortConfig.order : 'asc'}
                                        onClick={() => handleSort('DisplayedNumber')}
                                    >
                                        {t('list.table.columns.id')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sortDirection={sortConfig.orderBy === 'CreatedAt' ? sortConfig.order : false}
                                >
                                    <TableSortLabel
                                        active={sortConfig.orderBy === 'CreatedAt'}
                                        direction={sortConfig.orderBy === 'CreatedAt' ? sortConfig.order : 'desc'}
                                        onClick={() => handleSort('CreatedAt')}
                                    >
                                        {t('list.table.columns.date')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortConfig.orderBy === 'Title' ? sortConfig.order : false}>
                                    <TableSortLabel
                                        active={sortConfig.orderBy === 'Title'}
                                        direction={sortConfig.orderBy === 'Title' ? sortConfig.order : 'asc'}
                                        onClick={() => handleSort('Title')}
                                    >
                                        {t('list.table.columns.title')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortConfig.orderBy === 'Site' ? sortConfig.order : false}>
                                    <TableSortLabel
                                        active={sortConfig.orderBy === 'Site'}
                                        direction={sortConfig.orderBy === 'Site' ? sortConfig.order : 'asc'}
                                        onClick={() => handleSort('Site')}
                                    >
                                        {t('list.table.columns.site')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortConfig.orderBy === 'Status' ? sortConfig.order : false}>
                                    <TableSortLabel
                                        active={sortConfig.orderBy === 'Status'}
                                        direction={sortConfig.orderBy === 'Status' ? sortConfig.order : 'asc'}
                                        onClick={() => handleSort('Status')}
                                    >
                                        {t('list.table.columns.status')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{t('list.table.columns.action')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTickets?.map((ticket) => {
                                const siteNames = getSiteNames(ticket)
                                return (
                                    <TableRow
                                        key={ticket.DisplayedNumber}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleTicketClick(ticket)}
                                    >
                                        <TableCell>
                                            {' '}
                                            <Badge
                                                color="secondary"
                                                variant="dot"
                                                overlap="rectangular"
                                                invisible={!hasUnreadAdminMessages(ticket)}
                                            >
                                                {ticket.DisplayedNumber}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{formatDate(ticket.CreatedAt)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {ticket.Title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {siteNames.length ? (
                                                siteNames.map((name, index) => (
                                                    <Typography
                                                        key={`${ticket.DisplayedNumber}-${index}`}
                                                        variant="body2"
                                                    >
                                                        {name}
                                                    </Typography>
                                                ))
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {ticket.Status && (
                                                <Chip
                                                    label={ticket.Status}
                                                    color={getStatusColor(ticket.Status)}
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleTicketClick(ticket)
                                                }}
                                            >
                                                {t('list.table.viewDetails')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>

                    {filteredTickets.length === 0 && !loading && (
                        <Box p={4} textAlign="center">
                            <Typography color="text.secondary">
                                {searchQuery ? t('list.empty.search') : t('list.empty.default')}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    )
}
